import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import SubmitForm from "@/pages/submit/components/SubmitForm";
import { useSeo } from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";

const tips = [
  {
    icon: "ri-map-pin-time-line",
    title: "把时间线写下来",
    desc: "哪一天发生了什么，按顺序列出来。时间点越清楚，核查越快。",
  },
  {
    icon: "ri-attachment-2",
    title: "材料先别急着删",
    desc: "聊天记录、订单截图、通知照片都很关键，提交后也请保留原始文件。",
  },
  {
    icon: "ri-user-forbid-line",
    title: "不要写上自己的名字",
    desc: "如果需要说明身份，请使用化名或只描述身份，不要暴露真实姓名与单位。",
  },
  {
    icon: "ri-question-answer-line",
    title: "写清楚你的疑问",
    desc: "你希望被关注的点是什么？把这个问题说出来，编辑更容易抓住重点。",
  },
];

export default function SubmitPage() {
  useSeo({
    title: "我要投稿 | 网络爆料匿名投稿通道 - 直曝",
    description:
      "直曝在线投稿通道：填写线索标题、类别与内容即可匿名提交，无需注册，联系方式可留空。网络爆料投稿、匿名私信、邮箱与后台线索多种方式，全程保护投稿人隐私安全。",
    keywords: "网络爆料投稿,网络爆料匿名,网络爆料后台,网络爆料线索,匿名投稿",
    path: "/submit",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "我要投稿 - 直曝匿名投稿通道",
      url: "https://wzwzww.cn/submit",
      inLanguage: "zh-CN",
      description:
        "填写线索标题、类别与内容即可匿名提交，无需注册，联系方式可留空，全程保护投稿人隐私。",
      isPartOf: {
        "@type": "WebSite",
        name: siteInfo.fullName,
        url: "https://wzwzww.cn/",
      },
    },
  });

  return (
    <>
      <div className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs items={[{ label: "我要投稿" }]} />
            <h1 className="mt-5 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
              我要投稿：把你知道的事，交给编辑
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              这是一个匿名投稿表单。你可以只写一句话，也可以附上完整的经过。联系方式是选填的，留空同样会被认真阅读。
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            <SubmitForm />
          </div>

          <aside className="lg:col-span-4 flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">投稿小贴士</h2>
              <ul className="mt-5 space-y-5">
                {tips.map((tip) => (
                  <li key={tip.title} className="flex items-start gap-3.5">
                    <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0">
                      <i className={`${tip.icon} text-lg`} aria-hidden="true"></i>
                    </span>
                    <span className="flex-1">
                      <span className="block text-sm font-heading font-bold text-foreground-950">{tip.title}</span>
                      <span className="mt-1 block text-sm text-foreground-600 leading-relaxed">{tip.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-lg bg-foreground-950 text-background-50 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold flex items-center gap-2">
                <i className="ri-mail-send-line w-5 h-5 flex items-center justify-center text-accent-400" aria-hidden="true"></i>
                邮箱与后台通道
              </h2>
              <p className="mt-3 text-sm text-background-300 leading-relaxed">
                材料较多、或不想填表时，可以直接发邮件，也可以在站内后台给我们发私信。
              </p>
              <p className="mt-4 text-sm font-label text-accent-400 break-all">
                <a href={`mailto:${siteInfo.email}`} className="hover:underline cursor-pointer">
                  {siteInfo.email}
                </a>
              </p>
              <p className="mt-3 text-xs text-background-300/80 font-label">{siteInfo.workTime}</p>
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-2 text-sm font-label text-background-50 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部联系方式
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" aria-hidden="true"></i>
              </Link>
            </section>

            <section className="rounded-lg border border-secondary-300 bg-secondary-50 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-shield-check-line w-5 h-5 flex items-center justify-center text-secondary-700" aria-hidden="true"></i>
                隐私承诺
              </h2>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                投稿记录与内容库分离存放，原始材料仅两名值班编辑可见。发布前统一匿名化，投稿人身份不会出现在任何一篇报道里。
              </p>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}